<template>
  <div>
    <h1>{{ $t('RECEIPTS.TITLE') }}</h1>

    <v-layout row wrap align-center>
      <v-flex xs12 sm4 md3>
        <v-select
          v-model="month"
          :items="monthOptions"
          :label="$t('RECEIPTS.MONTH')"
        />
      </v-flex>
      <v-flex xs12 sm4 md3>
        <v-select
          v-model="year"
          :items="yearOptions"
          :label="$t('RECEIPTS.YEAR')"
        />
      </v-flex>
      <v-flex xs12 sm4 md3>
        <tk-btn :loading="loading" @click="fetchData(1)">{{
          $t('RECEIPTS.SEARCH')
        }}</tk-btn>
      </v-flex>
    </v-layout>

    <div v-if="loading" class="text-xs-center my-5 pt-5">
      <v-progress-circular :size="50" color="black" indeterminate />
    </div>

    <div v-else>
      <template v-if="receipts.length">
        <div class="receipts-table-wrapper">
          <table class="receipts-table">
            <thead>
              <tr>
                <th class="text-xs-left">{{ $t('RECEIPTS.ORDER_NUMBER') }}</th>
                <th class="text-xs-left">{{ $t('RECEIPTS.ORDER_DATE') }}</th>
                <th class="text-xs-left">{{ $t('RECEIPTS.RECEIPT_DATE') }}</th>
                <th class="text-xs-left">{{ $t('RECEIPTS.ARTWORK_TITLE') }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="receipt in receipts" :key="receipt.id">
                <td>{{ receipt.order_number }}</td>
                <td>{{ formatDate(receipt.order_date) }}</td>
                <td>{{ formatDate(receipt.receipt_date) }}</td>
                <td>{{ receipt.artwork_title }}</td>
                <td class="text-xs-right">
                  <tk-btn
                    small
                    flat
                    :loading="receipt.downloading"
                    @click="download(receipt)"
                  >{{ $t('RECEIPTS.DOWNLOAD') }}</tk-btn>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p class="mt-3">
          {{ $t('RECEIPTS.TOTAL', { count: pagination.total }) }}
        </p>

        <div v-if="pagination.lastPage > 1" class="text-xs-right mt-4">
          <v-pagination
            @input="fetchData"
            :disabled="loading"
            :value="pagination.currentPage"
            :length="pagination.lastPage"
            :total-visible="7"
          />
        </div>
      </template>

      <p v-else class="mt-4">{{ $t('RECEIPTS.EMPTY') }}</p>
    </div>
  </div>
</template>

<script>
import { RepositoryFactory } from "@/services/RepositoryFactory";
import { ErrorHandler } from "@/utils/ErrorHandler";

const receiptsRepo = RepositoryFactory.receipts();
const handler = new ErrorHandler();

export default {
  data() {
    const now = new Date();

    return {
      month: now.getMonth() + 1,
      year: now.getFullYear(),
      years: [],
      receipts: [],
      pagination: {},
      loading: true,
    };
  },

  computed: {
    intlLocale() {
      return { fi: "fi-FI", en: "en-GB", se: "sv-SE" }[this.$i18n.locale] || "fi-FI";
    },

    monthOptions() {
      const format = new Intl.DateTimeFormat(this.intlLocale, { month: "long" });

      return Array.from({ length: 12 }, (unused, index) => ({
        value: index + 1,
        text: format.format(new Date(2000, index, 1)),
      }));
    },

    yearOptions() {
      return this.years.length ? this.years : [new Date().getFullYear()];
    },
  },

  created() {
    this.fetchData(1);
  },

  methods: {
    async fetchData(page) {
      this.loading = true;
      await handler.handle(async () => {
        const res = await receiptsRepo.profileReceipts(this.month, this.year, page);
        this.receipts = res.data;
        this.years = res.years;
        this.pagination = res.pagination;
      });
      this.loading = false;
    },

    formatDate(value) {
      return value ? new Date(value).toLocaleDateString(this.intlLocale) : "";
    },

    async download(receipt) {
      this.$set(receipt, "downloading", true);

      try {
        const blob = await receiptsRepo.downloadReceipt(receipt.id);

        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `receipt-order-${receipt.order_number}-${receipt.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Failed to download receipt.", error);
      } finally {
        receipt.downloading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.receipts-table-wrapper {
  overflow-x: auto;
}

.receipts-table {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 8px 12px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    white-space: nowrap;
  }
}
</style>
